/**
 * Slice 49 / R1 — TIME ON SITE at the top of the owner's finished-job view.
 * Start time, finish time, the total (finish minus start, computed), each stamp's map link,
 * the distance in plain words, a red flag only when a stamp is beyond the away distance,
 * and a grey "Location not saved" where there is no location.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchStampSettings } from "@/lib/stampOutbox";
import {
  formatClockTime,
  formatDurationWords,
  minutesBetween,
  stampDistanceWords,
  stampMapUrl,
  stampReasonWords,
} from "@/lib/locationStamps";
import { cn } from "@/lib/utils";

export interface TimeOnSiteTask {
  started_at?: string | null;
  completed_at?: string | null;
  start_location_lat?: number | null;
  start_location_lng?: number | null;
  start_location_distance_m?: number | null;
  start_location_verified?: boolean | null;
  start_location_override_reason?: string | null;
  finish_location_lat?: number | null;
  finish_location_lng?: number | null;
  finish_location_distance_m?: number | null;
  finish_location_verified?: boolean | null;
  finish_location_override_reason?: string | null;
}

type StampView = {
  label: "Started" | "Finished";
  at: string | null;
  lat: number | null;
  lng: number | null;
  distanceM: number | null;
  away: boolean | null;
  reason: string | null;
};

function toView(task: TimeOnSiteTask, which: "start" | "finish", awayDistanceM: number): StampView {
  const start = which === "start";
  const lat = (start ? task.start_location_lat : task.finish_location_lat) ?? null;
  const lng = (start ? task.start_location_lng : task.finish_location_lng) ?? null;
  const distanceM = (start ? task.start_location_distance_m : task.finish_location_distance_m) ?? null;
  const verified = (start ? task.start_location_verified : task.finish_location_verified) ?? null;
  const away = verified != null ? !verified : distanceM != null ? distanceM > awayDistanceM : null;
  return {
    label: start ? "Started" : "Finished",
    at: (start ? task.started_at : task.completed_at) ?? null,
    lat,
    lng,
    distanceM,
    away,
    reason: (start ? task.start_location_override_reason : task.finish_location_override_reason) ?? null,
  };
}

function StampTile({ stamp }: { stamp: StampView }) {
  const hasLocation = stamp.lat != null && stamp.lng != null;
  const away = stamp.away === true;
  const words = hasLocation ? stampDistanceWords(stamp.distanceM, stamp.away) : null;
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-[14px]",
        away ? "border-[1.5px] border-[#e9c2bd]" : "border border-[#e6edf1]",
      )}
    >
      {hasLocation ? (
        <a
          href={stampMapUrl(stamp.lat!, stamp.lng!)}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex h-[120px] items-center justify-center bg-[repeating-linear-gradient(135deg,#eef3f6_0_8px,#e3eaef_8px_16px)]"
        >
          <span
            className={cn(
              "h-5 w-5 rounded-full border-4 border-white shadow-[0_2px_8px_rgba(15,23,32,0.25)]",
              away ? "bg-[#b4453a]" : "bg-[#24a89a]",
            )}
          />
          <span className="absolute right-2 top-2 rounded-full bg-white px-[9px] py-1 text-[12px] font-bold text-[#15776d]">
            Open map ↗
          </span>
        </a>
      ) : (
        <div className="flex h-[120px] items-center justify-center border-b border-dashed border-[#cdd8df] bg-[#f4f7f9]">
          <span className="text-[14px] font-bold text-[#8494a2]">Location not saved</span>
        </div>
      )}
      <div className="flex flex-col gap-[3px] px-3.5 py-3">
        <div className="text-[12px] font-bold text-[#8494a2]">{stamp.label}</div>
        <div className="text-[17px] font-bold text-[#0f1720]">{stamp.at ? formatClockTime(stamp.at) : "—"}</div>
        {hasLocation ? (
          words ? (
            <div className={cn("text-[13px]", away ? "font-bold text-[#b4453a]" : "font-medium text-[#64748b]")}>
              {words}
            </div>
          ) : null
        ) : (
          stampReasonWords(stamp.reason) && (
            <div className="text-[13px] font-medium text-[#8494a2]">{stampReasonWords(stamp.reason)}</div>
          )
        )}
      </div>
    </div>
  );
}

export function TimeOnSiteSection({ propertyId, task }: { propertyId: string; task: TimeOnSiteTask | null }) {
  const { data: settings } = useQuery({
    queryKey: ["stamp-settings", propertyId],
    queryFn: () => fetchStampSettings(propertyId),
    staleTime: 60_000,
  });
  if (!task || (!task.started_at && !task.completed_at)) return null;

  const awayM = settings?.awayDistanceM ?? 300;
  const start = toView(task, "start", awayM);
  const finish = toView(task, "finish", awayM);
  const total = task.started_at && task.completed_at ? minutesBetween(task.started_at, task.completed_at) : null;

  const anyLocation = start.lat != null || finish.lat != null;
  const stampsOff = settings?.enabled === false && !anyLocation && !start.reason && !finish.reason;

  const startedAway = start.away === true;
  const finishedAway = finish.away === true;
  const awayText =
    startedAway && finishedAway
      ? "Started and finished away from the property."
      : startedAway
        ? "Started away from the property."
        : finishedAway
          ? "Finished away from the property."
          : null;

  return (
    <div className="flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-5" data-testid="time-on-site">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">TIME ON SITE</div>
        <div className="text-[18px] font-bold text-[#0f1720]">{total != null ? formatDurationWords(total) : "—"}</div>
      </div>
      {stampsOff ? (
        <div className="grid grid-cols-2 gap-3">
          {[start, finish].map((s) => (
            <div key={s.label} className="flex flex-col gap-[3px] rounded-[14px] border border-[#e6edf1] px-3.5 py-3">
              <div className="text-[12px] font-bold text-[#8494a2]">{s.label}</div>
              <div className="text-[17px] font-bold text-[#0f1720]">{s.at ? formatClockTime(s.at) : "—"}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {awayText ? (
            <div className="flex items-center gap-2.5 rounded-xl bg-[#fbefed] px-3.5 py-3">
              <span className="h-2 w-2 shrink-0 rounded-full bg-[#b4453a]" />
              <span className="text-[14.5px] font-bold text-[#b4453a]">{awayText}</span>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            <StampTile stamp={start} />
            <StampTile stamp={finish} />
          </div>
        </>
      )}
    </div>
  );
}
